<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">User Balances at Date</h4>
        <div class="text-muted">
          Reconstruct all user account balances as of a specific point in time
          (Financial Reconciliation)
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingUserBalancesAtDate"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- FILTERS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-label>Select Date</b-form-label>
            <b-input-group>
              <flat-pickr
                v-model="selectedDate"
                class="form-control"
                :config="flatPickrConfig"
                @on-change="onDateChange"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="onDateChange">
                  <feather-icon icon="SearchIcon" size="16" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- STATS -->
    <b-card class="mb-3" v-if="selectedDate">
      <div>
        <b-row>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Users</div>
              <div class="h5 font-weight-bold">{{ metadata.total || 0 }}</div>
            </div>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Balance</div>
              <div class="h5 font-weight-bold text-success">
                {{ formatCurrencyBuiltInFunc(totalBalance || 0) }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body v-if="selectedDate">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="d-inline-block mx-1"
              style="width: 100px"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-form-input v-model="search" placeholder="Search by email..." />
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredItems"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingUserBalancesAtDate"
      >
        <template #cell(userId)="{ item }">
          <b-link :to="{ name: 'customers-view', params: { id: item.userId } }">
            {{ item.userId }}
          </b-link>
        </template>

        <template #cell(name)="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>

        <template #cell(balance)="{ item }">
          <span class="font-weight-semibold">{{
            formatCurrencyBuiltInFunc(item.balance)
          }}</span>
        </template>

        <template #cell(balanceAsAt)="{ item }">
          <div class="small">{{ formatDateTime(item.balanceAsAt) }}</div>
        </template>
      </b-table>

      <div class="mx-2 mb-2 mt-2" v-if="metadata.total > 0">
        <b-row>
          <b-col cols="12" sm="6">
            <span class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }} to
              {{ Math.min(currentPage * perPage, metadata.total) }} of
              {{ metadata.total }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex justify-content-end"
            >
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.total"
              :per-page="perPage"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <b-card v-else class="text-center text-muted py-4">
      Select a date to view user balances
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BLink,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BFormLabel,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapState } from "vuex";
import { format } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BLink,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BFormLabel,
    vSelect,
    flatPickr,
    BPagination,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      search: "",
      selectedDate: format(new Date(), "yyyy-MM-dd"),
      flatPickrConfig: {
        dateFormat: "Y-m-d",
      },
      tableFields: [
        { key: "userId", label: "User ID" },
        { key: "email", label: "Email" },
        { key: "name", label: "Name" },
        { key: "balance", label: "Balance" },
        { key: "balanceAsAt", label: "As At Date" },
      ],
      metadata: { total: 0 },
    };
  },
  computed: {
    ...mapState("reporting", [
      "userBalancesAtDate",
      "loadingUserBalancesAtDate",
      "userBalancesAtDatePagination",
    ]),
    filteredItems() {
      if (!this.search) return this.userBalancesAtDate;
      const s = this.search.toLowerCase();
      return this.userBalancesAtDate.filter(
        (item) =>
          item.email?.toLowerCase().includes(s) ||
          item.firstName?.toLowerCase().includes(s) ||
          item.lastName?.toLowerCase().includes(s)
      );
    },
    totalBalance() {
      return (this.userBalancesAtDate || []).reduce(
        (sum, item) => sum + (Number(item.balance) || 0),
        0
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchData();
    },
  },
  methods: {
    ...mapActions("reporting", ["getUserBalancesAtDate"]),
    formatCurrencyBuiltInFunc,
    async onDateChange() {
      if (!this.selectedDate) return;
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      if (!this.selectedDate) return;
      const data = await this.getUserBalancesAtDate({
        date: this.selectedDate,
        page: this.currentPage,
        limit: this.perPage,
      });
      if (data?.pagination) {
        this.metadata = {
          total: data.pagination.total || 0,
        };
      }
    },
    formatDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },
    formatDateTime(date) {
      return format(new Date(date), "dd/MM/yyyy HH:mm:ss");
    },

    handleExport() {
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
